<template>
  <div class="mt-10">
    <div class="elevation-2 px-5 mt-3">
      <v-row>
        <v-col cols="12">
          <SimplePageHeader title="Weekly View" class="px-4 pr-5 mb-5"/>
        </v-col>
      </v-row>
      <v-form ref="filterForm">
        <v-row
          align="center"
          justify="start"
          class="px-5"
        >
          <v-col cols="12" sm="5" md="3" lg="3">
            <WeekPicker
              :date="dateFilter"
              @weekChange="weekChangeHandler"
              @dateChange="(newValue) => this.dateFilter = newValue"
            />
            <div v-if="dateFilterError" class="error--text caption mt-1 ml-1">Week is required</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <BusinessEntitiesDropDownList
              v-model="businessEntityFilter"
              :clearable="true"
              @businessEntityIdChange="handleBusinessEntityChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <ClientsDropDownList
              :id="clientFilter"
              :businessEntityId="businessEntityFilter"
              :clearable="true"
              @clientIdChange="handleClientIdChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <ClientConsultantsDropDownList
              v-model="consultantFilter"
              :clientId="clientFilter"
              :disabled="!clientFilter"
              clearable
              outlined
            />
          </v-col>
        </v-row>
        <v-row justify="end" class="px-5 pb-2">
          <v-col cols="12" sm="6">
            <FilterButtons
              @search="handleSearchClick"
              @clear="handleClearClick"
            ></FilterButtons>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <FilterButtonsSmall
      v-if="!showSaveChangesButton"
      @search="handleSearchClick"
      @clear="handleClearClick"
    ></FilterButtonsSmall>
    <v-row class="mt-5">
      <v-col cols="12">
        <v-card style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
          <v-card-text>
            <div
              class="px-4 pt-5"
            >
              <SimplePageHeader title="Consultants"></SimplePageHeader>
              <v-row class="hide-on-print">
                <v-col cols="12" align="right">
                  <ToggleButton
                    :state="collapseButtonState"
                    color="primary"
                    :statesLabel="collapseButtonLabels"
                    @click="(state) => this.collapseButtonState = state === 1 ? 2 : 1 "
                  ></ToggleButton>
                </v-col>
              </v-row>
              <v-data-table
                dense
                :headers="mainHeaders"
                :items="assignmentsTimeSheetEntriesValue"
                item-key="clientId"
                :sort-by="['clientName']"
                :sort-desc="[false]"
                hide-default-footer
                show-expand
                :expanded="expanded"
                :items-per-page="-1"
              >
                <template v-slot:expanded-item="{ headers, item }">
                  <td class="py-5" :colspan="headers.length">
                    <v-data-table
                      dense
                      :headers="subHeaders"
                      :items="item.assignmentsTimeSheetEntries"
                      class="pb-3"
                    >
                      <template v-for="(slot,i) of subHeaders" v-slot:[`item.${slot.value}`]="{ item }">
                        <span
                          :key="`text-${i}`"
                          v-if="i === 0 || i === 1" >
                          {{ item[`${slot.value}`] }}
                          <span v-if="i === 0" style="color:#808080;">
                            <br>
                            {{ item['totalTime'] | formatDecimal }} hours
                            <template v-if="item.timeOffTime > 0">
                              <br>
                              Time Off: {{ item.timeOffTime | formatDecimal }} hours
                            </template>
                          </span>
                        </span>
                        <span
                          :key="`commission-${i}`"
                          v-else-if="i === 2" >
                          <FormattedNumberField
                            class="my-3"
                            prepend-inner-icon="attach_money"
                            :value="item[`${slot.value}`]"
                            :required="false"
                            :dense="true"
                            :validateOnBlur="true"
                            :hideDetails="true"
                            :disabled="!item.contractorCommissionEnabled"
                            @valueChange="
                              contractorCommissionChangeHandler(
                                item,
                                $event,
                                slot.value,
                                item.assignmentId
                              )"
                          ></FormattedNumberField>
                        </span>
                        <div
                          :key="`hours-${i}`"
                          v-else
                        >
                          <span v-if="item[`${slot.value}`].hours === null || item[`${slot.value}`].hours === undefined"></span>
                          <div v-else-if="hasLabeledEntries(item[`${slot.value}`])" class="multi-type-cell">
                            <div
                              v-for="entry in item[`${slot.value}`].entries"
                              :key="entry.timeTypeId"
                              class="caption"
                            >
                              {{ timeTypeCode(entry.timeTypeId) }} {{ entry.hours | formatDecimal }}
                            </div>
                          </div>
                          <FormattedNumberField
                            v-else
                            class="my-3"
                            :value="item[`${slot.value}`].hours"
                            :required="false"
                            :dense="true"
                            :validateOnBlur="true"
                            :hideDetails="true"
                            @valueChange="
                              timeValueChangeHandler(
                                item,
                                $event,
                                slot.value,
                                item.assignmentId,
                                item.consultantId,
                                null
                              )"
                          ></FormattedNumberField>
                        </div>
                      </template>
                    </v-data-table>
                  </td>
                </template>
                <template v-slot:item.totalTime="{ item }">
                  {{ item.totalTime | formatDecimal }} hours
                  <template v-if="item.timeOffTime > 0">
                    <br>
                    Time Off: {{ item.timeOffTime | formatDecimal }} hours
                  </template>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="staffTimeManagementItems != null && staffTimeManagementItems.length > 0">
      <v-col cols="12">
        <v-card style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
          <v-card-text>
            <div
              class="px-4 pt-5"
            >
              <SimplePageHeader title="Internal Employees"></SimplePageHeader>
              <v-container fluid class="px-0 pt-2 mt-4">
                <v-data-table
                  dense
                  :headers="staffHeaders"
                  :items="staffTimeManagementItems"
                  class="pb-3 px-5"
                >
                  <template v-for="(slot,i) of staffHeaders" v-slot:[`item.${slot.value}`]="{ item }">
                    <span
                      :key="`text-${i}`"
                      v-if="i === 0" >
                      {{ item[`${slot.value}`] }}
                      <span style="color:#808080;">
                        <br>
                        {{ item['totalTime'] | formatDecimal }} hours
                      </span>
                    </span>
                    <div
                      :key="`hours-${i}`"
                      v-else
                    >
                      <span v-if="item[`${slot.value}`].hours === null || item[`${slot.value}`].hours === undefined"></span>
                      <FormattedNumberField
                        v-else
                        class="my-3"
                        :value="item[`${slot.value}`].hours"
                        :required="false"
                        :dense="true"
                        :validateOnBlur="true"
                        :hideDetails="true"
                        @valueChange="
                          timeValueChangeHandler(
                            item,
                            $event,
                            slot.value,
                            null,
                            item.userId,
                            item.staffId
                          )"
                      ></FormattedNumberField>
                    </div>
                  </template>
                </v-data-table>
              </v-container>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <ButtonsSubmitForm
      v-if="showSaveChangesButton"
      :isFormValid="true"
      acceptLabel="Save Changes"
      cancelLabel="Discard"
      @save="saveChanges"
      @cancel="discardChanges"
    ></ButtonsSubmitForm>
    <div
      v-if="showSaveChangesButton"
      class="hide-on-md hide-on-print mt-5 abs-left-0"
    >
      <v-container fluid class="fixed-buttons mt-0 px-0">
        <v-layout row>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              color="primary"
              @click="saveChanges"
              >
              Save Changes
            </v-btn>
          </v-flex>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              outlined
              @click="discardChanges"
              >
              Discard
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { ToggleButton, ButtonsSubmitForm, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { WeekPicker } from '@/components/shared/datePickers'
import { FormattedNumberField } from '@/components/shared/textFields'
import excel from '@/lib/export/excel'
import { formatDate } from '@/lib/display'
import { timeTypeCode } from '@/lib/constants/timeTypes'
import { hasLabeledEntries, rosterWorkedHours } from './weeklyTimeManagementHelpers'
import { ClientsDropDownList, ClientConsultantsDropDownList } from '@/components/clients'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  name: 'WeeklyTimeManagement',
  components: {
    SimplePageHeader,
    WeekPicker,
    ToggleButton,
    FormattedNumberField,
    ButtonsSubmitForm,
    FilterButtons,
    FilterButtonsSmall,
    ClientConsultantsDropDownList,
    ClientsDropDownList,
    BusinessEntitiesDropDownList
  },
  data () {
    return {
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      mainHeaders: [
        { text: 'Client', value: 'clientName', sortable: false, cellClass: 'py-5' },
        { text: 'Total Hours', value: 'totalTime', sortable: false, cellClass: 'py-5', align: 'right' }
      ],
      collapseButtonLabels: {
        1: 'Contract All',
        2: 'Expand All'
      },
      collapseButtonState: 1, // 1 - Expanded, 2 - Collapsed
      businessEntityFilter: null,
      appliedBusinessEntityFilter: null,
      clientFilter: null,
      appliedClientFilter: null,
      consultantFilter: null,
      appliedConsultantFilter: null,
      timeSheetEntriesToSave: [],
      contractorCommissionsToSave: [],
      dateFilterError: false,
      initialLoadDone: false
    }
  },
  computed: {
    ...mapFields('timeManagement', [
      'timeManagementClientItems',
      'staffTimeManagementItems'
    ]),
    showSaveChangesButton () {
      return (this.timeSheetEntriesToSave && this.timeSheetEntriesToSave.length > 0) ||
        (this.contractorCommissionsToSave && this.contractorCommissionsToSave.length > 0)
    },
    currentWeek () {
      return this.dateFilter >= new Date(this.dateBeginFilter) && this.dateFilter <= new Date(this.dateEndFilter)
    },
    subHeaders: {
      get () {
        let headers = [
          { text: 'Consultant', value: 'consultantName', sortable: true, width: '130px', class: 'px-1', cellClass: 'px-1' },
          { text: 'Job Title', value: 'jobTitle', sortable: true, width: '130px', class: 'px-1', cellClass: 'px-1' },
          { text: 'Contractor Commission', value: 'contractorCommission', sortable: false, class: 'contractor-commission-col', cellClass: 'contractor-commission-col' }
        ]
        for (const item in this.timeManagementClientItems[0].assignmentsTimeSheetEntries[0]) {
          headers.push({
            text: item,
            value: item,
            align: 'center',
            sortable: false,
            width: '50px',
            cellClass: 'time-management-col',
            class: 'time-management-col'
          })
        }
        return headers.filter(
          header => header.text !== 'assignmentId' &&
            header.text !== 'clientId' &&
            header.text !== 'clientName' &&
            header.text !== 'consultantId' &&
            header.text !== 'consultantName' &&
            header.text !== 'jobTitle' &&
            header.text !== 'totalTime' &&
            header.text !== 'contractorCommissionId' &&
            header.text !== 'contractorCommission' &&
            header.text !== 'contractorCommissionEnabled'
        )
      }
    },
    staffHeaders: {
      get () {
        let headers = [
          { text: 'Employee', value: 'employeeName', sortable: true, width: '160px', class: 'px-1', cellClass: 'px-1' }
        ]
        for (const item in this.staffTimeManagementItems[0]) {
          headers.push({
            text: item,
            value: item,
            align: 'center',
            sortable: false,
            width: '15px',
            cellClass: 'px-1'
          })
        }
        return headers.filter(
          header => header.text !== 'staffId' &&
            header.text !== 'employeeName' &&
            header.text !== 'totalTime' &&
            header.text !== 'userId'
        )
      }
    },
    expanded: {
      get () {
        if (this.collapseButtonState === 1) {
          return this.assignmentsTimeSheetEntriesValue || []
        } else {
          return []
        }
      }
    },
    assignmentsTimeSheetEntriesValue () {
      let itemsToShow = this.timeManagementClientItems
      if (this.appliedConsultantFilter) {
        itemsToShow = itemsToShow.map(client => {
          let tempClient = JSON.parse(JSON.stringify(client))
          tempClient.assignmentsTimeSheetEntries = tempClient.assignmentsTimeSheetEntries.filter(
            assignment => assignment.consultantId === this.appliedConsultantFilter
          )
          if (tempClient.assignmentsTimeSheetEntries.length > 0) {
            return tempClient
          }
        })
        itemsToShow = itemsToShow.filter(client => client !== null && client !== undefined)
      }
      return itemsToShow
    }
  },
  watch: {
    '$route.query' (query) {
      const nextDateBegin = query.dateBegin || null
      const nextDateEnd = query.dateEnd || null
      const nextBusinessEntityFilter = Number(query.businessEntityId) || null
      const nextClientFilter = Number(query.clientId) || null
      const nextConsultantFilter = Number(query.consultantId) || null
      const filtersChanged = this.dateBeginFilter !== nextDateBegin ||
        this.dateEndFilter !== nextDateEnd ||
        this.appliedBusinessEntityFilter !== nextBusinessEntityFilter ||
        this.appliedClientFilter !== nextClientFilter ||
        this.appliedConsultantFilter !== nextConsultantFilter

      if (filtersChanged) {
        this.dateBeginFilter = nextDateBegin
        this.dateEndFilter = nextDateEnd
        const parsedDateBegin = nextDateBegin ? new Date(nextDateBegin) : null
        this.dateFilter = parsedDateBegin && !isNaN(parsedDateBegin.getTime()) ? parsedDateBegin : null
        this.businessEntityFilter = nextBusinessEntityFilter
        this.clientFilter = nextClientFilter
        this.consultantFilter = nextConsultantFilter
        this.appliedBusinessEntityFilter = nextBusinessEntityFilter
        this.appliedClientFilter = nextClientFilter
        this.appliedConsultantFilter = nextConsultantFilter
        if (nextDateBegin) {
          this.initialLoadDone = true
          this.loadTime(nextDateBegin, nextDateEnd)
        }
      }
    }
  },
  created () {
    const { dateBegin, dateEnd, businessEntityId, clientId, consultantId } = this.$route.query
    const parsedDateBegin = dateBegin ? new Date(dateBegin) : null
    this.dateFilter = parsedDateBegin && !isNaN(parsedDateBegin.getTime()) ? parsedDateBegin : null
    this.dateBeginFilter = dateBegin || null
    this.dateEndFilter = dateEnd || null
    this.businessEntityFilter = Number(businessEntityId) || null
    this.clientFilter = Number(clientId) || null
    this.consultantFilter = Number(consultantId) || null
    this.appliedBusinessEntityFilter = this.businessEntityFilter
    this.appliedClientFilter = this.clientFilter
    this.appliedConsultantFilter = this.consultantFilter
    this.syncQueryToRoute()
    if (this.dateBeginFilter && this.dateEndFilter) {
      this.initialLoadDone = true
      this.loadTime(this.dateBeginFilter, this.dateEndFilter)
    }
  },
  methods: {
    formatDate,
    timeTypeCode,
    hasLabeledEntries,
    ...mapActions('timeManagement', [
      'getTimeManagementData',
      'getStaffTimeManagementData',
      'saveTimeSheetEntries',
      'saveContractorCommissions'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime'
    ]),
    loadTime (dateBegin, dateEnd) {
      const filters = {
        staffLynxDateBegin: dateBegin,
        staffLynxDateEnd: dateEnd
      }
      if (this.appliedBusinessEntityFilter !== null && this.appliedBusinessEntityFilter !== undefined) {
        filters.businessEntityId = this.appliedBusinessEntityFilter
      }
      if (this.appliedClientFilter !== null && this.appliedClientFilter !== undefined) {
        filters.clientId = this.appliedClientFilter
      }

      this.getTimeManagementData(filters)
        .then(() => {
          this.getStaffTimeManagementData({ staffLynxDateBegin: dateBegin, staffLynxDateEnd: dateEnd }).then((staffTimeManagementItems) => {
            this.staffTimeManagementItems = staffTimeManagementItems
            this.staffTimeManagementItems.sort((a, b) => (a.employeeName > b.employeeName) ? 1 : -1)
          })
        })
      this.timeSheetEntriesToSave = []
      this.contractorCommissionsToSave = []
    },
    handleSearchClick () {
      const formValid = this.$refs.filterForm.validate()
      this.dateFilterError = !this.dateBeginFilter
      if (!formValid || this.dateFilterError) return
      this.appliedBusinessEntityFilter = this.businessEntityFilter
      this.appliedClientFilter = this.clientFilter
      this.appliedConsultantFilter = this.consultantFilter
      this.syncQueryToRoute()
      this.loadTime(this.dateBeginFilter, this.dateEndFilter)
    },
    handleClearClick () {
      this.businessEntityFilter = null
      this.appliedBusinessEntityFilter = null
      this.clientFilter = null
      this.consultantFilter = null
      this.appliedClientFilter = null
      this.appliedConsultantFilter = null
      this.dateFilterError = false
      this.$refs.filterForm.resetValidation()
      this.syncQueryToRoute()
      if (this.dateBeginFilter && this.dateEndFilter) {
        this.loadTime(this.dateBeginFilter, this.dateEndFilter)
      }
    },
    handleBusinessEntityChange () {
      this.clientFilter = null
      this.consultantFilter = null
    },
    handleClientIdChange (newValue) {
      this.clientFilter = newValue
      this.consultantFilter = null
    },
    weekChangeHandler (dateBegin, dateEnd) {
      this.dateFilterError = false
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      this.syncQueryToRoute()
      if (!this.initialLoadDone) {
        this.initialLoadDone = true
        this.loadTime(dateBegin, dateEnd)
      }
    },
    handleExportToExcel () {
      let data = []
      let objectKeys = []
      if (this.assignmentsTimeSheetEntriesValue.length > 0) {
        for (let client of this.assignmentsTimeSheetEntriesValue) {
          for (let assignment of client.assignmentsTimeSheetEntries) {
            let regExp = /[0-9]{2}\/[0-9]{2}/
            objectKeys = Object.keys(assignment)
            objectKeys = objectKeys.filter(key => regExp.test(key))
            let row = {
              client: client.clientName,
              consultant: assignment.consultantName
            }
            for (let date of objectKeys) {
              row[formatDate(assignment[`${date}`].date)] =
                assignment[`${date}`].hours !== null && assignment[`${date}`].hours !== undefined
                  ? parseFloat(assignment[`${date}`].hours).toFixed(2) : ''
            }
            data.push(row)
          }
        }
      }
      data.sort((a, b) => a.client.localeCompare(b.client) || a.consultant.localeCompare(b.consultant))
      if (data.length > 0) {
        let headers = Object.keys(data[0]).map(header => {
          return header.charAt(0).toUpperCase() + header.slice(1)
        })
        let columns = Object.keys(data[0])
        const params = {
          title: headers,
          key: columns,
          data: data,
          autoWidth: true,
          filename: `Time`
        }
        excel.exportArray2Excel(params)
      }
    },
    timeValueChangeHandler (item, hours, dateLabel, assignmentId, userId, staffId) {
      // If the time has changed.
      if (parseFloat(item[`${dateLabel}`].hours) !== parseFloat(hours) && hours !== null && hours !== undefined) {
        item[`${dateLabel}`].hours = parseFloat(hours)
        item['totalTime'] = rosterWorkedHours(item)

        if (!staffId) {
          var tempClients = this.timeManagementClientItems.filter(client => client.clientId === item.clientId)
          if (tempClients != null && tempClients.length > 0) {
            tempClients[0].totalTime = tempClients[0].assignmentsTimeSheetEntries
              .reduce((sum, assignment) => sum + rosterWorkedHours(assignment), 0)
          }
        }

        let tempTimeSheetEntryToSave = {
          assignmentId: assignmentId,
          userId: userId,
          hours: hours,
          date: item[`${dateLabel}`].date,
          dateLabel: dateLabel,
          id: item[`${dateLabel}`].timeSheetEntryId,
          staffId: staffId
        }
        let existingEntry = this.timeSheetEntriesToSave.filter(
          tse => tse.assignmentId === tempTimeSheetEntryToSave.assignmentId &&
            tse.dateLabel === tempTimeSheetEntryToSave.dateLabel
        )
        if (existingEntry && existingEntry.length > 0) {
          existingEntry[0].hours = tempTimeSheetEntryToSave.hours
        } else {
          this.timeSheetEntriesToSave.push(tempTimeSheetEntryToSave)
        }
      }
    },
    contractorCommissionChangeHandler (item, newValue) {
      // Update the visible state
      const originalValue = item.contractorCommission

      item.contractorCommission = newValue

      // Find existing entry by assignmentId
      const existingEntry = this.contractorCommissionsToSave
        .find(e => e.assignmentId === item.assignmentId)

      // Case 1: entry exists -> update it
      if (existingEntry) {
        existingEntry.commission = newValue
        existingEntry.updatedAt = new Date()
        return
      }

      // Case 2: This prevents creating entries when the user focus the
      // field but doesn't change the value.
      if (originalValue === newValue) {
        return
      }

      // Case 3: entry doesn't exist AND value > 0 -> create it
      this.contractorCommissionsToSave.push({
        id: item.contractorCommissionId,
        year: 0,
        week: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 0,
        updatedBy: 0,
        assignmentId: item.assignmentId,
        commission: newValue
      })
    },
    async saveChanges () {
      try {
        await this.saveTimeSheetEntries(this.timeSheetEntriesToSave)

        if (this.contractorCommissionsToSave.length > 0) {
          await this.saveContractorCommissions({
            contractorCommissionsPerWeek: this.contractorCommissionsToSave,
            staffLynxDateBegin: this.dateBeginFilter,
            staffLynxDateEnd: this.dateEndFilter
          })
        }

        await this.loadTime(this.dateBeginFilter, this.dateEndFilter)
      } catch (error) {
        // Swallow. The error is handled in the store action with a notification.
      }
    },
    discardChanges () {
      this.loadTime(this.dateBeginFilter, this.dateEndFilter)
    },
    syncQueryToRoute () {
      const nextQuery = this.syncFiltersToQuery({ ...this.$route.query })
      if (hasQueryChanged(this.$route.query, nextQuery, ['dateBegin', 'dateEnd', 'businessEntityId', 'clientId', 'consultantId'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    syncFiltersToQuery (nextQuery = { ...this.$route.query }) {
      setOrDeleteQueryParam(nextQuery, 'dateBegin', this.dateBeginFilter)
      setOrDeleteQueryParam(nextQuery, 'dateEnd', this.dateEndFilter)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.appliedBusinessEntityFilter)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.appliedClientFilter)
      setOrDeleteQueryParam(nextQuery, 'consultantId', this.appliedConsultantFilter)
      return nextQuery
    }
  }
}
</script>

<style>
  .multi-type-cell {
    line-height: 1.2;
    white-space: nowrap;
  }
</style>
